import { create } from "zustand";
import { persist } from "zustand/middleware";
import { FramingRow } from '../types/contentFraming';
import { SlideShapeInfo } from "../types/contentFraming";

// Function to get input text from localStorage metadata
function getInputTextFromMetadata(rowId: string): string {
  try {
    if (typeof window === "undefined" || !("localStorage" in window)) {
      return '';
    }
    const raw = localStorage.getItem("metadata");
    if (!raw) return '';
    
    const parsed = JSON.parse(raw);
    
    // Map row IDs to metadata keys
    const keyMap: { [key: string]: string } = {
      'slideTitle': 'slideTitle',
      'slideSubTitle': 'slideSubTitle', 
      'placeholderTitle': 'chartTitle',
      'placeholderSubTitle': 'chartSubTitle',
      'source': 'source',
      'notes': 'notes',
      'keyTakeAways': 'keyTakeAways',
      'script': 'script'
    };
    
    const key = keyMap[rowId];
    if(key === 'placeholderTitle' || key === 'placeholderSubTitle'){
      return "";
    }
    if (key && parsed[key]) {
      if(key === 'notes'){
        const notes = parsed[key].value.split('\n'); 
        let notesWithNumbers = [];
        if(notes.length > 0){
          notesWithNumbers = notes.map((note) =>  `${note}`);
          notesWithNumbers.unshift("Notes:");
          return notesWithNumbers.join('\n');
        }else{
          notesWithNumbers = ["Notes:"];
          return notesWithNumbers.join('\n');
        }
      } else if(key === 'source'){
        const source = `Source: ${parsed[key].value}`; 
        return source;
      }
      return String(parsed[key].value);
    }
    
    return '';
  } catch {
    return '';
  }
}

// Initial data for the 6 rows
const initialRows: FramingRow[] = [
    {
      id: 'slideTitle',
      label: 'Slide Title:',
      enabled: false,
      selection: '',
      disabledText: '(Empty)',
      inputText: '',
      mode: 'placeholderDesign',
      placeholder:{
        shapeId: '',
        slideId: '',
        name: '',
        left: 0,
        top: 0,
        width: 0,
        height: 0
      }
    },
    {
      id: 'slideSubTitle',
      label: 'Slide Sub-Title:',
      enabled: false,
      selection: '',
      disabledText: '(Empty)',
      inputText: '',
      mode: 'placeholderDesign',
      placeholder:{
        shapeId: '',
        slideId: '',
        name: '',
        left: 0,
        top: 0,
        width: 0,
        height: 0
      }
    },
    {
      id: 'placeholderTitle',
      label: 'Placeholder Title:',
      enabled: false,
      selection: '',
      disabledText: '(Empty)',
      inputText: '',
      mode: 'placeholderDesign',
      placeholder:{
        shapeId: '',
        slideId: '',
        name: '',
        left: 0,
        top: 0,
        width: 0,
        height: 0
      }
    },
    {
      id: 'placeholderSubTitle',
      label: 'Placeholder Subtitle:',
      enabled: false,
      selection: '',
      disabledText: '(Empty)',
      inputText: '',
      mode: 'placeholderDesign',
      placeholder:{
        shapeId: '',
        slideId: '',
        name: '',
        left: 0,
        top: 0,
        width: 0,
        height: 0
      }
    },
    {
      id: 'source',
      label: 'Source:',
      enabled: false,
      selection: 'Source_RightHalf_Bottom', // Default for Source row
      disabledText: '(Empty)',
      inputText: '',
      mode: 'placeholderDesign',
      placeholder:{
        shapeId: '',
        slideId: '',
        name: '',
        left: 0,
        top: 0,
        width: 0,
        height: 0
      }
    },
    {
      id: 'notes',
      label: 'Notes:',
      enabled: false,
      selection: '',
      disabledText: '(Empty)',
      inputText: '',
      mode: 'placeholderDesign',
      placeholder:{
        shapeId: '',
        slideId: '',
        name: '',
        left: 0,
        top: 0,
        width: 0,
        height: 0
      }
    },
    {
      id: 'keyTakeAways',
      label: 'Key Take-Aways:',
      enabled: false,
      selection: '',
      disabledText: '(Empty)',
      inputText: '',
      mode: 'placeholderDesign',
      placeholder:{
        shapeId: '',
        slideId: '',
        name: '',
        left: 0,
        top: 0,
        width: 0,
        height: 0
      }
    },
    {
      id: 'script',
      label: 'Script:',
      enabled: true,
      disabledText: '(Empty)',
      inputText: '',
      mode: 'placeholderDesign',
      placeholder:{
        shapeId: '',
        slideId: '',
        name: '',
        left: 0,
        top: 0,
        width: 0,
        height: 0
      }
    }

  ];


interface ContentFramingStoreState {
  
  // Step management
  currentStep: number;
  answersByStep: number[];
  questionsPerStep: number[];
  stepLabels: string[];
  stepSizes: number[];
  
  // Actions for step management
  setCurrentStep: (step: number) => void;
  nextStep: () => void;
  prevStep: () => void;
  markAnswered: (step: number) => void;
  updateAnswered: (step: number, value: number) => void;
  resetStore: () => void;


  // Content Framing Store Data and Actions
  rows: FramingRow[];
  slideShapesData: SlideShapeInfo[];
  slideDimensions: { width: number; height: number } | null;
  placeholderOptions: { value: string; label: string }[];
  hoveredShapeId: string | null;
  metadataApplied: boolean;
  setHoveredShapeId: (shapeId: string | null) => void;
  toggleRow: (id: string, enabled: boolean) => void;
  setSelection: (id: string, value: string) => void;
  setInputText: (id: string, value: string) => void;
  setMode: (id: string, mode: 'formatTemplate' | 'placeholderDesign') => void;
  setAllEnabled: (enabled: boolean) => void;
  reset: () => void;
  updateFieldsFromShape: (id: string, shapeId: string) => void;
  loadInputTextFromMetadata: () => void;
  setSlideShapesData: (data: SlideShapeInfo[]) => void;
  setSlideDimensions: (dims: { width: number; height: number } | null) => void;
  updateRowsFromLabeledShapes: (shapes: SlideShapeInfo[]) => void;

  // Co-Pilot (Alpha Agent) UI state
  copilot: {
    isOpen: boolean;
    rowId: string | null;
    x: number;
    y: number;
    mode: 'IconButton' | 'AlphaCo-pilot' | 'Loading' | 'ControlBox' | '';
  };
  openCopilot: (rowId: string, x: number, y: number) => void;
  closeCopilot: () => void;
  setCopilotMode: (mode: 'IconButton' | 'AlphaCo-pilot' | 'Loading' | 'ControlBox' | '') => void;

  // Convenience helpers for getting/setting row input text by id
  getRowInputText: (rowId: string) => string;
  setRowInputText: (rowId: string, value: string) => void;
  getRowContext: (rowId: string) => string;

  // Copilot history stacks (undo/redo of generated text)
  copilotHistory: {
    backStack: string[];
    forwardStack: string[];
  };
  historyPush: (text: string) => void;
  historyBack: () => string | null;
  historyForward: () => string | null;
  historyReset: () => void;
}

function loadPlaceholderOptionsFromLocalStorage(shapes: SlideShapeInfo[]): { value: string; label: string }[] {
    const filtered = shapes.filter(s => 
      s && 
      typeof s.left === 'number' && 
      typeof s.top === 'number' && 
      s.label !== 'Target Shape'
    );
    
    // Step 1: Sort by left position
    const leftSorted = filtered.slice().sort((a, b) => a.left - b.left);
    
    // Step 2: Sort by top position within each left group
    const sorted = leftSorted.sort((a, b) => {
      return a.top - b.top;
    });
      
    const shapeOptions = sorted.map(s => ({
      value: String(s.id),
      label: String(s.label),
    }));

    return [{value: "", label: "Select a placeholder"}, ...shapeOptions];
}

// Build placeholder options excluding any shapes already selected in rows
function buildPlaceholderOptions(
  shapes: SlideShapeInfo[],
  excludedShapeIds: Set<string>
): { value: string; label: string }[] {
  const base = loadPlaceholderOptionsFromLocalStorage(shapes);
  const filtered = base.filter(opt => opt.value === "" || !excludedShapeIds.has(opt.value));
  return filtered;
}


export const useContentFramingStore = create<ContentFramingStoreState>()(
  persist(
    (set, get) => ({
      // Initial state
      currentStep: 0,
      answersByStep: [1, 0],
      questionsPerStep: [1, 2],
      stepLabels: ["Title & Sourcing", "Key Take-Aways & Script"],
      stepSizes: [1, 2],

      
      // Step management actions
      setCurrentStep: (step: number) => {
        set({ currentStep: step });
      },
      
      nextStep: () => {
        const { currentStep, stepLabels } = get();
        if (currentStep < stepLabels.length - 1) {
          set({ currentStep: currentStep + 1 });
        }
      },
      
      prevStep: () => {
        const { currentStep } = get();
        if (currentStep > 0) {
          set({ currentStep: currentStep - 1 });
        }
      },
      
      markAnswered: (step: number) => {
        const { answersByStep, questionsPerStep } = get();
        const newAnswers = [...answersByStep];
        newAnswers[step] = Math.min(newAnswers[step] + 1, questionsPerStep[step]);
        set({ answersByStep: newAnswers });
      },
      
      updateAnswered: (step: number, value: number) => {
        const { answersByStep } = get();
        const newAnswers = [...answersByStep];
        newAnswers[step] = value;
        set({ answersByStep: newAnswers });
      },
      
      resetStore: () => {
        set({
          currentStep: 0,
          answersByStep: [1, 0],
        });
      },


      // Content Framing Store Data and Actions
      rows: initialRows,
      placeholderOptions: loadPlaceholderOptionsFromLocalStorage([]),
      slideShapesData: [],
      slideDimensions: { width: 0, height: 0 },
      hoveredShapeId: null,
      metadataApplied: false,
      
      setHoveredShapeId: (shapeId: string | null) => {
        set({ hoveredShapeId: shapeId });
      },
 
      toggleRow: (id: string, enabled: boolean) => {
          set((state) => ({
            rows: state.rows.map(row => 
              row.id === id ? { ...row, enabled } : row
            )
          }));
        },
        
        setSelection: (id: string, value: string) => {
          set((state) => {
            const newRows = state.rows.map(row => 
              row.id === id ? { ...row, selection: value, enabled: value ? true : false } : row
            );
            const selectedIds = new Set<string>(
              newRows.map(r => r.selection).filter((s): s is string => Boolean(s))
            );
            return {
              rows: newRows,
              placeholderOptions: buildPlaceholderOptions(state.slideShapesData, selectedIds)
            };
          });
        },

        updateFieldsFromShape: (id: string, shapeId: string) => {
          set((state) => {
            // Find the selected shape data
            const selectedShape = state.slideShapesData.find(shape => shape.id === shapeId);

            if (selectedShape) {
              const newRows = state.rows.map(row => 
                row.id === id ? { 
                  ...row, 
                  selection: shapeId,
                  enabled: true,
                  disabledText: selectedShape.text || "(Empty)",
                  placeholder: {
                    shapeId: selectedShape.id,
                    slideId: selectedShape.slideId,
                    name: selectedShape.name,
                    left: selectedShape.left,
                    top: selectedShape.top,
                    width: selectedShape.width,
                    height: selectedShape.height
                  }
                } : row
              );
              const selectedIds = new Set<string>(
                newRows.map(r => r.selection).filter((s): s is string => Boolean(s))
              );
              return {
                rows: newRows,
                placeholderOptions: buildPlaceholderOptions(state.slideShapesData, selectedIds)
              };
            }
            
            return state;
          });
        },
        
        setInputText: (id: string, value: string) => {
          set((state) => ({
            rows: state.rows.map(row => 
              row.id === id ? { ...row, inputText: value } : row
            ),
            metadataApplied: true,
          }));
        },
        
        setMode: (id: string, mode: 'formatTemplate' | 'placeholderDesign') => {
          set((state) => ({
            rows: state.rows.map(row => 
              row.id === id ? { ...row, mode } : row
            )
          }));
        },
        
        setAllEnabled: (enabled: boolean) => {
          set((state) => ({
            rows: state.rows.map(row => ({ ...row, enabled }))
          }));
        },
        
        reset: () => {
          set({ rows: initialRows });
          set({ placeholderOptions: [] });
          set({ slideShapesData: [] });
          set({ slideDimensions: { width: 0, height: 0 } });
          set({ metadataApplied: false });
          set({currentStep: 0});
          set({answersByStep: [1, 0]});
        },

        loadInputTextFromMetadata: () => {
          set((state) => {
            if (state.metadataApplied) return state;
            return {
              rows: state.rows.map(row => ({
                ...row,
                inputText: row.inputText && row.inputText.trim() !== ''
                  ? row.inputText
                  : getInputTextFromMetadata(row.id)
              })),
              metadataApplied: true,
            };
          });
        },

        setSlideShapesData: (data: SlideShapeInfo[]) => {
          set((state) => {
            const selectedIds = new Set<string>(
              state.rows.map(r => r.selection).filter((s): s is string => Boolean(s))
            );
            return {
              slideShapesData: data,
              placeholderOptions: buildPlaceholderOptions(data, selectedIds)
            };
          });
        },

        setSlideDimensions: (dims: { width: number; height: number } | null) => {
          set({ slideDimensions: dims });
        },

        updateRowsFromLabeledShapes: (shapes: SlideShapeInfo[]) => {
          set((state: any) => {
            // Map labels to row IDs
            const labelToRowIdMap: { [key: string]: string } = {
              'Slide Title': 'slideTitle',
              'Slide Subtitle': 'slideSubTitle',
              'Placeholder Title': 'placeholderTitle',
              'Placeholder Subtitle': 'placeholderSubTitle',
              'Source': 'source',
              'Notes': 'notes',
              'Key Take-Aways': 'keyTakeAways',
              'Script': 'script'
            };

            // Get slideId from first shape (all shapes should be from the same slide)
            const slideId = shapes.length > 0 ? shapes[0].slideId : null;

            const updatedRows = state.rows.map(row => {
              // Find the shape that matches this row's label
              const matchingShape = shapes.find(shape => {
                const rowId = labelToRowIdMap[shape.label || ''];
                return rowId === row.id;
              });

              if (matchingShape) {
                return {
                  ...row,
                  selection: matchingShape.id,
                  enabled: true,
                  disabledText: matchingShape.text || "(Empty)",
                  placeholder: {
                    shapeId: matchingShape.id,
                    slideId: matchingShape.slideId,
                    name: matchingShape.name,
                    left: matchingShape.left,
                    top: matchingShape.top,
                    width: matchingShape.width,
                    height: matchingShape.height
                  }
                };
              }

              // For 'script' row, set slideId even if there's no matching shape
              if (row.id === 'script' && slideId) {
                return {
                  ...row,
                  enabled: true,
                  placeholder: {
                    shapeId: '', // No shape for script
                    slideId: slideId,
                    name: 'Script',
                    left: 0,
                    top: 0,
                    width: 0,
                    height: 0
                  }
                };
              }

              return row;
            });

            const selectedIds = new Set<string>(
              updatedRows.map((r: any) => r.selection).filter((s: any): s is string => Boolean(s))
            );
            console.log("Updated Rows", updatedRows);
            return { 
              rows: updatedRows,
              placeholderOptions: buildPlaceholderOptions(state.slideShapesData, selectedIds)
            };
          });
        },

      // Co-Pilot state
      copilot: { isOpen: false, rowId: null, x: 0, y: 0, mode: '' },
      openCopilot: (rowId: string, x: number, y: number) => {
        const { copilot } = get();
        set({ copilotHistory: { backStack: [], forwardStack: [] } });
        set({ copilot: { ...copilot, isOpen: true, rowId, x, y, mode: 'IconButton' } });
      },
      closeCopilot: () => {
        set({ copilot: { isOpen: false, rowId: null, x: 0, y: 0, mode: '' } });
        set({ copilotHistory: { backStack: [], forwardStack: [] } });
      },
      setCopilotMode: (mode) => {
        const { copilot } = get();
        set({ copilot: { ...copilot, mode } });
      },

      // Copilot history management
      copilotHistory: { backStack: [], forwardStack: [] },
      historyPush: (text: string) => {
        set((state) => ({
          copilotHistory: {
            backStack: [...state.copilotHistory.backStack, text],
            forwardStack: [],
          }
        }));
      },
      historyBack: () => {
        const { copilotHistory } = get();
        if (copilotHistory.backStack.length <= 1) return null; // keep at least initial
        const newBack = copilotHistory.backStack.slice(0, -1);
        const popped = copilotHistory.backStack[copilotHistory.backStack.length - 1];
        set({ copilotHistory: { backStack: newBack, forwardStack: [popped, ...copilotHistory.forwardStack] } });
        return newBack[newBack.length - 1] ?? null;
      },
      historyForward: () => {
        const { copilotHistory } = get();
        if (copilotHistory.forwardStack.length === 0) return null;
        const [next, ...rest] = copilotHistory.forwardStack;
        set({ copilotHistory: { backStack: [...copilotHistory.backStack, next], forwardStack: rest } });
        return next;
      },
      historyReset: () => {
        set({ copilotHistory: { backStack: [], forwardStack: [] } });
      },

      // Helpers for reading/writing input text by row id
      getRowInputText: (rowId: string) => {
        const { rows } = get();
        const row = rows.find(r => r.id === rowId);
        return row?.inputText ?? '';
      },
      setRowInputText: (rowId: string, value: string) => {
        const { setInputText } = get();
        setInputText(rowId, value);
      },

      getRowContext: (rowId: string) => {
        const { rows } = get();
        const row = rows.find(r => r.id === rowId);
        return row?.inputText + ' ' + row?.disabledText;
      },
    }),
    {
      name: 'content-framing-store',
      version: 2,
      // Only persist the data we want to keep
      partialize: (state) => ({
        currentStep: state.currentStep,
        answersByStep: state.answersByStep,
        questionsPerStep: state.questionsPerStep,
        rows: state.rows,
        slideShapesData: state.slideShapesData,
        slideDimensions: state.slideDimensions,
      }),
    }
  )
);

